import { useModalContentToggler } from '@components'

import { Flex, Icon, Space, Text } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { fetchBankAccounts } from '@features/bankAccounts/query-api'
import { fetchAccounts } from '@modules-deprecated/app/accounts/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../../config/queryClient'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { QuickStartStep } from '../../../../../quickStart/enums/quickStartStep'
import { useQuickStartProgress } from '../../../../../quickStart/hooks'
import { useFetchShineBankAccounts } from '../../../../hooks/useFetchShineBankAccounts'
import { useShineConnectModal } from '../../contexts/shineConnectModalContext'
import { CreateBankConnectionModalSteps } from '../../enums/createBankConnectionModalSteps'
import * as Styled from './styles'

export const ProcessingModalBody = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { isMerging, hasMergeFailed, hasMergeSucceeded, setShineBankAccounts, shineBankAccounts } =
    useShineConnectModal()
  const { setActiveState } = useModalContentToggler()
  const { markStepDone } = useQuickStartProgress()
  const hasRefetchedRef = useRef(false)
  const [shouldStopPolling, setShouldStopPolling] = useState(false)
  const [isRefetchingAccounts, setIsRefetchingAccounts] = useState(false)

  const refetchAndProceed = useCallback(async () => {
    if (!organization?.id) {
      setActiveState(CreateBankConnectionModalSteps.Error)
      return
    }

    setIsRefetchingAccounts(true)

    try {
      await queryClient.cancelQueries(QueryKeys.Accounts)
      await Promise.all([
        queryClient.fetchQuery([QueryKeys.Accounts, organization.id], () => fetchAccounts(organization.id)),
        queryClient.fetchQuery([QueryKeys.BankAccounts], () => fetchBankAccounts(organization.id)),
      ])

      markStepDone(QuickStartStep.Bank)
      setActiveState(CreateBankConnectionModalSteps.SuccessNewFlow)
    } catch {
      setActiveState(CreateBankConnectionModalSteps.Error)
    } finally {
      setIsRefetchingAccounts(false)
    }
  }, [markStepDone, setActiveState, organization?.id])

  const {
    shineBankAccounts: fetchedShineBankAccounts,
    isPolling,
    hasPolled,
  } = useFetchShineBankAccounts({
    polling: !!hasMergeSucceeded && !shouldStopPolling,
  })

  useEffect(() => {
    if (fetchedShineBankAccounts.length > 0) {
      setShineBankAccounts(fetchedShineBankAccounts)
    }
  }, [fetchedShineBankAccounts, setShineBankAccounts])

  useEffect(() => {
    if (hasMergeFailed) {
      setActiveState(CreateBankConnectionModalSteps.Error)
      return
    }

    if (hasMergeSucceeded && !isPolling && hasPolled && !hasRefetchedRef.current) {
      if (shineBankAccounts.length > 0) {
        hasRefetchedRef.current = true
        setShouldStopPolling(true)
        refetchAndProceed()
      } else {
        // Polling finished but no accounts found
        setActiveState(CreateBankConnectionModalSteps.Error)
      }
    }
  }, [
    hasMergeFailed,
    hasMergeSucceeded,
    shineBankAccounts.length,
    isPolling,
    hasPolled,
    organization?.id,
    setActiveState,
    refetchAndProceed,
  ])

  return (
    <Flex flexDirection="column" alignItems="center">
      <Space size="s32" />
      <Styled.IconWrapper isLoading={isMerging || isPolling || isRefetchingAccounts}>
        <Icon icon="circleDashed" color="currentColor" />
      </Styled.IconWrapper>
      <Space size="s40" />
      <Text weight="medium" variant="h3">
        {t('bank_connection.shine_connect_modal.processing_title')}
      </Text>
      <Space size="s8" />
      <Text variant="bodyLG" colorVariant="discreet">
        {t('bank_connection.shine_connect_modal.processing_description')}
      </Text>
      <Space size="s80" />
    </Flex>
  )
}
